import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import _ from "lodash";

import { useAppDispatch, useAppSelector } from "../../store/hooks.ts";

import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";

import {
	setEventEditMode,
	clearSelectedEvent,
	selectOriginalEvent,
	selectSelectedEvent,
	saveEventEdits,
	resetEventEdits,
	updateEventField,
	setSelectedEvent,
} from "../../store/slices/eventSlice.ts";
import {
	setSelectedEntityType,
	updateEvent,
	updateSession,
} from "../../store/slices/calendarSlice.ts";

import {
	Box,
	Typography,
	Button,
	TextField,
	IconButton,
	Tooltip,
	MenuItem,
	Menu,
	Autocomplete,
	CircularProgress,
	Grid,
	FormControlLabel,
	Checkbox,
} from "@mui/material";

import PersonIcon from "@mui/icons-material/Person";
import SettingsIcon from "@mui/icons-material/Settings";

import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";

import DeleteIcon from "@mui/icons-material/Delete";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import ContentPasteIcon from "@mui/icons-material/ContentPaste";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import SaveIcon from "@mui/icons-material/Save";

import AddCircleIcon from "@mui/icons-material/AddCircle";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import ListboxComponent from "../../components/listbox/ListBox.tsx";
import globalAxios from "../../globalAxios.js";
import { SERVER_URL } from "../../config.js";

import {
	setSnackbarOpen,
	setSnackbarSeverity,
	setSnackbarText,
} from "../../store/slices/snackbarSlice.ts";

import {
	setSelectedSession,
	selectSelectedSession,
} from "../../store/slices/sessionSlice.ts";

import { playSuccessSave } from "../../components/sounds/index.ts";

import {
	setLoadingDetail,
	setSelectedItem,
} from "../../store/slices/searchSlice.ts";

import { calculateSinglePtFeeEstimate } from "../calendar/helper/feeCalculation.ts";
import { selectUserSettings } from "../../store/slices/userSlice.ts";

import { resolveSurgeonBillingRule } from "../calendar/helper/surgeonBillingRules.ts";
import { setSurgeonBillingRulesForSurgeon } from "../../store/slices/surgeonBillingRulesSlice.ts";

import { determineEventFundingStatus } from "../calendar/helper/fundingStatus.ts";
import { alpha } from "@mui/material/styles";

import { upsertSchedules } from "../../store/slices/schedulesSlice.ts";
import { SPECIALTY_OPTIONS_BASE } from "../../constants/index.ts";

const EventView = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const userSettings = useAppSelector(selectUserSettings);

	const currentLens = useAppSelector((state) => state.calendar.lens);

	const isFundingLens = currentLens === "fundingStatus";

	const schedules = useAppSelector((state) => state.schedules.data);

	const validItemNumbers = useMemo(
		() => new Set(schedules.map((s) => String(s.item_num))),
		[schedules],
	);

	const event = useAppSelector(selectSelectedEvent);

	const originalEvent = useAppSelector(selectOriginalEvent);

	console.log("====", event);

	const selectedSession = useAppSelector(selectSelectedSession);

	const surgeonBillingRulesBySurgeonId = useAppSelector(
		(state) => state.surgeonBillingRules.rulesBySurgeonId,
	);

	const billingSurgeonId = useMemo(() => {
		const rawSurgeonId =
			event?.surgeon_id ??
			selectedSession?.surgeon_id ??
			selectedSession?.surgeon?.id;

		if (rawSurgeonId === null || rawSurgeonId === undefined) {
			return null;
		}

		const parsedSurgeonId = Number(rawSurgeonId);

		return Number.isFinite(parsedSurgeonId) ? parsedSurgeonId : null;
	}, [
		event?.surgeon_id,
		selectedSession?.surgeon_id,
		selectedSession?.surgeon?.id,
	]);

	const surgeonBillingRules = useMemo(() => {
		if (billingSurgeonId === null) {
			return [];
		}

		const rules = surgeonBillingRulesBySurgeonId[billingSurgeonId];

		return Array.isArray(rules) ? rules : [];
	}, [billingSurgeonId, surgeonBillingRulesBySurgeonId]);

	const [billingRulesLoading, setBillingRulesLoading] = useState(false);

	const [billingRulesLoadedForSurgeonId, setBillingRulesLoadedForSurgeonId] =
		useState<number | null>(null);

	const [billingRulesLoadError, setBillingRulesLoadError] = useState<
		string | null
	>(null);

	const hasManualOopOverride = useMemo(() => {
		const value = event?.proposed_oop;

		return (
			value !== undefined &&
			value !== null &&
			String(value).trim() !== "" &&
			Number.isFinite(Number(value))
		);
	}, [event?.proposed_oop]);

	const matchedBillingRule = useMemo(() => {
		if (!event) {
			return null;
		}

		if (billingSurgeonId === null) {
			return null;
		}

		if (billingRulesLoadedForSurgeonId !== billingSurgeonId) {
			return null;
		}

		return resolveSurgeonBillingRule(
			event,
			surgeonBillingRules,
			selectedSession,
		);
	}, [
		event,
		billingSurgeonId,
		billingRulesLoadedForSurgeonId,
		surgeonBillingRules,
		selectedSession,
	]);

	const resolvedDefaultOop = useMemo(() => {
		if (!matchedBillingRule) {
			return null;
		}

		const value = Number(matchedBillingRule.default_oop);

		return Number.isFinite(value) ? value : null;
	}, [matchedBillingRule]);

	useEffect(() => {
		if (billingSurgeonId === null) {
			setBillingRulesLoading(false);
			setBillingRulesLoadedForSurgeonId(null);
			setBillingRulesLoadError(null);
			return;
		}

		let cancelled = false;

		const extractRules = (payload: any) => {
			const candidates = [
				payload,
				payload?.rules,
				payload?.surgeonBillingRules,
				payload?.surgeon_billing_rules,
				payload?.billingRules,
				payload?.data,
				payload?.data?.rules,
				payload?.data?.surgeonBillingRules,
				payload?.data?.surgeon_billing_rules,
				payload?.data?.billingRules,
			];

			const rules = candidates.find((candidate) =>
				Array.isArray(candidate),
			);

			return Array.isArray(rules) ? rules : [];
		};

		const loadBillingRules = async () => {
			setBillingRulesLoading(true);

			setBillingRulesLoadError(null);

			setBillingRulesLoadedForSurgeonId(null);

			try {
				console.log("[OOP] Loading surgeon billing rules:", {
					surgeonId: billingSurgeonId,
				});

				const response = await globalAxios.get(
					`${SERVER_URL}/surgeon-billing-rules/${billingSurgeonId}`,
				);

				if (cancelled) {
					return;
				}

				console.log(
					"[OOP] Surgeon billing rules raw response:",
					response.data,
				);

				const rules = extractRules(response.data);

				console.log("[OOP] Surgeon billing rules loaded:", {
					surgeonId: billingSurgeonId,
					count: rules.length,
					rules,
				});

				dispatch(
					setSurgeonBillingRulesForSurgeon({
						surgeonId: billingSurgeonId,
						rules,
					}),
				);

				setBillingRulesLoadedForSurgeonId(billingSurgeonId);
			} catch (error: any) {
				if (cancelled) {
					return;
				}

				console.error(
					"[OOP] Failed to load surgeon billing rules:",
					error,
				);

				setBillingRulesLoadError(
					error?.response?.data?.message ||
						error?.message ||
						"Failed to load surgeon billing rules.",
				);
			} finally {
				if (!cancelled) {
					setBillingRulesLoading(false);
				}
			}
		};

		loadBillingRules();

		return () => {
			cancelled = true;
		};
	}, [billingSurgeonId, dispatch]);

	const eventIndex = useMemo(() => {
		if (!selectedSession || !event) {
			return -1;
		}

		return selectedSession.events.findIndex((e) => e.id === event.id);
	}, [selectedSession, event]);

	const isEditing = useAppSelector((state) => state.event.isEditing);

	const [healthFunds, setHealthFunds] = useState([]);

	const [anaesthetists, setAnaesthetists] = useState([]);

	const [loadingHealthFunds, setLoadingHealthFunds] = useState(false);

	const [loadingAnaesthetists, setLoadingAnaesthetists] = useState(false);

	const [savingItems, setSavingItems] = useState(false);

	const [saveItemsSuccess, setSaveItemsSuccess] = useState(false);

	const [localItems, setLocalItems] = useState(() => {
		const raw = event?.actual_items;

		if (!raw || raw === "null") {
			return [];
		}

		try {
			const parsed = JSON.parse(raw);

			return Array.isArray(parsed) ? parsed : [];
		} catch (e) {
			console.error("Failed to parse actual_items:", e);

			return [];
		}
	});

	const originalItems = useMemo(() => {
		return JSON.parse(originalEvent?.actual_items || "[]");
	}, [originalEvent?.actual_items]);

	useEffect(() => {
		if (event?.actual_items) {
			const items = Array.isArray(event.actual_items)
				? event.actual_items
				: JSON.parse(event.actual_items);

			setLocalItems(items.map((i) => i.toString()));

			setNewItem("");
		}
	}, [event]);

	const [newItem, setNewItem] = useState("");

	const handleCancelItemChanges = () => {
		if (!originalItems) {
			return;
		}

		setLocalItems([...originalItems]);
	};

	const [anchorMenu, setAnchorMenu] = useState(null);

	const openMenu = Boolean(anchorMenu);

	const [savingEvent, setSavingEvent] = useState(false);

	const [saveSuccess, setSaveSuccess] = useState(false);

	const [deletingEvent, setDeletingEvent] = useState(false);

	useEffect(() => {
		if (
			isEditing &&
			(healthFunds.length === 0 || anaesthetists.length === 0)
		) {
			setLoadingHealthFunds(true);
			setLoadingAnaesthetists(true);

			Promise.all([
				globalAxios.get(`${SERVER_URL}/healthfunds`),
				globalAxios.get(`${SERVER_URL}/anaesthetists`),
			])
				.then(([fundsRes, anaesRes]) => {
					setHealthFunds(fundsRes.data.healthFunds || []);
					setAnaesthetists(anaesRes.data.anaesthetists || []);
				})
				.catch((error) => {
					console.error("Error fetching dropdown data:", error);
				})
				.finally(() => {
					setLoadingHealthFunds(false);
					setLoadingAnaesthetists(false);
				});
		}
	}, [isEditing, healthFunds.length, anaesthetists.length]);

	const [oop, setOop] = useState<string>("0.00");

	const [oopTouched, setOopTouched] = useState<boolean>(false);

	const [oopSource, setOopSource] = useState<string>(
		"Loading surgeon billing rule...",
	);

	const [clearOopOnSave, setClearOopOnSave] = useState(false);

	useEffect(() => {
		setOopTouched(false);
		setClearOopOnSave(false);
	}, [event?.id]);

	const normaliseCurrency = (s: string) => {
		const clean = (s ?? "").replace(/[^\d.]/g, "");

		if (clean === "") {
			return "";
		}

		const n = Number(clean);

		return Number.isNaN(n) ? "" : n.toFixed(2);
	};

	useEffect(() => {
		if (!event) {
			return;
		}

		if (oopTouched) {
			return;
		}

		if (hasManualOopOverride) {
			const manualOop = Number(event.proposed_oop);

			setOop(manualOop.toFixed(2));

			setOopSource("Manual OOP");

			console.log("[OOP] Using manual override:", {
				eventId: event.id,
				proposed_oop: event.proposed_oop,
			});

			return;
		}

		if (billingSurgeonId === null) {
			setOop("0.00");

			setOopSource("No surgeon billing rule");

			return;
		}

		if (billingRulesLoadedForSurgeonId !== billingSurgeonId) {
			setOopSource(
				billingRulesLoading
					? "Loading surgeon billing rule..."
					: billingRulesLoadError
						? "Unable to load surgeon billing rule"
						: "Loading surgeon billing rule...",
			);

			return;
		}

		console.log("[OOP] Resolved default:", {
			eventId: event.id,
			surgeonId: billingSurgeonId,
			ruleCount: surgeonBillingRules.length,
			matchedBillingRule,
			resolvedDefaultOop,
		});

		if (resolvedDefaultOop !== null) {
			setOop(resolvedDefaultOop.toFixed(2));

			setOopSource("Surgeon billing rule");

			return;
		}

		setOop("0.00");
		setOopSource("No OOP");
	}, [
		event,
		oopTouched,
		hasManualOopOverride,
		billingSurgeonId,
		billingRulesLoadedForSurgeonId,
		billingRulesLoading,
		billingRulesLoadError,
		surgeonBillingRules,
		matchedBillingRule,
		resolvedDefaultOop,
	]);

	const eventForCalc = useMemo(() => {
		if (!event) {
			return null;
		}

		const resolvedOop = Number(oop);

		return {
			...event,
			proposed_oop: Number.isFinite(resolvedOop) ? resolvedOop : 0,
		};
	}, [event, oop]);

	const finalFee = useMemo(() => {
		if (!eventForCalc) {
			return 0;
		}

		const total =
			calculateSinglePtFeeEstimate(
				userSettings,
				eventForCalc,
				schedules,
			) ?? 0;

		return total;
	}, [userSettings, eventForCalc, schedules]);

	const [actualFee, setActualFee] = useState<string>("");

	const [actualFeeDirty, setActualFeeDirty] = useState<boolean>(false);

	useEffect(() => {
		const v = event?.actual_fee ?? "";

		setActualFee(v || "");
		setActualFeeDirty(false);
	}, [event?.actual_fee]);

	const changesMade = useMemo(() => {
		return !_.isEqual(event, originalEvent);
	}, [event, originalEvent]);

	const itemChangesMade = useMemo(() => {
		return !_.isEqual(localItems, originalItems);
	}, [localItems, originalItems]);

	const proposedOop = event?.proposed_oop;

	const serverActualFee = event?.actual_fee;

	const feeChangesMade = useMemo(() => {
		const oopChanged =
			clearOopOnSave ||
			(oopTouched &&
				oop !== "" &&
				Number(oop) !== Number(proposedOop ?? 0));

		const actualChanged =
			actualFeeDirty && actualFee !== String(serverActualFee ?? "");

		return oopChanged || actualChanged;
	}, [
		clearOopOnSave,
		oopTouched,
		oop,
		proposedOop,
		actualFeeDirty,
		actualFee,
		serverActualFee,
	]);

	if (!event) {
		return null;
	}

	const fundingStatus = determineEventFundingStatus(event);

	const fundingColours =
		userSettings?.calendar?.colorConfig?.fundingStatus || {};

	const fundingColour = (() => {
		switch (fundingStatus) {
			case "ifc_required":
				return fundingColours["IFC Required"] || "#d32f2f";

			case "funding_incomplete":
				return (
					fundingColours["Funding Incomplete"] ||
					fundingColours["Funding Missing"] ||
					"#ed6c02"
				);

			case "complete":
				return (
					fundingColours["Funding Complete"] ||
					fundingColours["Funding Available"] ||
					"#388e3c"
				);

			default:
				return "#757575";
		}
	})();

	const handleBack = () => {
		dispatch(clearSelectedEvent());

		dispatch(setSelectedEntityType("session"));
	};

	const handleMenuClick = (event) => {
		setAnchorMenu(event.currentTarget);
	};

	const handleMenuClose = () => {
		setAnchorMenu(null);
	};

	const handleItemChange = (index, value) => {
		const parsed = parseInt(value.trim(), 10);

		if (!isNaN(parsed)) {
			const updated = [...localItems];

			updated[index] = parsed;

			setLocalItems(updated);
		}
	};

	const handleAddItem = () => {
		const trimmed = newItem.trim();

		if (/^\d+$/.test(trimmed)) {
			setLocalItems([...localItems, trimmed]);

			setNewItem("");
		}
	};

	const handleDeleteItem = (index) => {
		const updated = [...localItems];

		updated.splice(index, 1);

		setLocalItems(updated);
	};

	const handleUpdateField = (fieldPath, value) => {
		if (eventIndex < 0) {
			return;
		}

		const pathParts = fieldPath.split(".");

		let updatedField = {};
		let current = updatedField;

		for (let i = 0; i < pathParts.length - 1; i++) {
			const key = isNaN(pathParts[i])
				? pathParts[i]
				: parseInt(pathParts[i]);

			current[key] = {};
			current = current[key];
		}

		const finalKey = pathParts[pathParts.length - 1];

		current[isNaN(finalKey) ? finalKey : parseInt(finalKey)] = value;

		dispatch(
			updateEventField({
				field: fieldPath,
				value,
			}),
		);
	};

	const handleToggleIFCField = async (
		field: "ifc_required" | "ifc_sent",
		checked: boolean,
	) => {
		if (!event || !selectedSession) {
			return;
		}

		const previousEvent = event;

		const optimisticEvent = {
			...event,
			[field]: checked,
			...(field === "ifc_required" && !checked
				? {
						ifc_sent: false,
					}
				: {}),
		};

		const optimisticEvents = selectedSession.events.map((ev) =>
			Number(ev.id) === Number(event.id) ? optimisticEvent : ev,
		);

		dispatch(setSelectedEvent(optimisticEvent));

		dispatch(
			setSelectedSession({
				...selectedSession,
				events: optimisticEvents,
			}),
		);

		dispatch(
			updateEvent({
				sessionId: selectedSession.id.toString(),
				eventId: Number(event.id),
				updates: optimisticEvent,
			}),
		);

		try {
			const payload =
				field === "ifc_required" && !checked
					? {
							ifc_required: false,
							ifc_sent: false,
						}
					: {
							[field]: checked,
						};

			console.log("[IFC] PATCH payload", payload);

			const response = await globalAxios.patch(
				`${SERVER_URL}/user/events/${event.id}`,
				payload,
			);

			const updatedEvent =
				response.data.updatedEvent || response.data.event;

			const updatedEvents = selectedSession.events.map((ev) =>
				Number(ev.id) === Number(updatedEvent.id) ? updatedEvent : ev,
			);

			dispatch(setSelectedEvent(updatedEvent));

			dispatch(
				setSelectedSession({
					...selectedSession,
					events: updatedEvents,
				}),
			);

			dispatch(
				updateEvent({
					sessionId: selectedSession.id.toString(),
					eventId: Number(updatedEvent.id),
					updates: updatedEvent,
				}),
			);
		} catch (err) {
			console.error("[IFC] update failed:", err);

			const rolledBackEvents = selectedSession.events.map((ev) =>
				Number(ev.id) === Number(previousEvent.id) ? previousEvent : ev,
			);

			dispatch(setSelectedEvent(previousEvent));

			dispatch(
				setSelectedSession({
					...selectedSession,
					events: rolledBackEvents,
				}),
			);

			dispatch(
				updateEvent({
					sessionId: selectedSession.id.toString(),
					eventId: Number(previousEvent.id),
					updates: previousEvent,
				}),
			);

			dispatch(setSnackbarOpen(true));

			dispatch(setSnackbarSeverity("error"));

			dispatch(setSnackbarText("Failed to update IFC."));
		}
	};

	const handleSaveEvent = async () => {
		if (!event || !selectedSession) {
			return;
		}

		setSavingEvent(true);

		const updatePayload = {
			procedure: event.procedure,
			specialty: event.specialty,
			anaesthetist: event.anaesthetist,
			patient: event.patient,
		};

		try {
			const response = await globalAxios.patch(
				`${SERVER_URL}/user/events/${event.id}`,
				updatePayload,
			);

			if (response.status === 200) {
				playSuccessSave();

				const updatedEvent = response.data.event;

				const updatedEvents = selectedSession.events.map((e) =>
					e.id === updatedEvent.id ? updatedEvent : e,
				);

				const updatedSession = {
					...selectedSession,
					events: updatedEvents,
				};

				dispatch(
					updateSession({
						sessionId: selectedSession.id,
						updates: {
							events: updatedEvents as [],
						},
					}),
				);

				dispatch(setSelectedSession(updatedSession));

				dispatch(saveEventEdits());

				setSaveSuccess(true);

				dispatch(setSnackbarOpen(true));

				dispatch(setSnackbarSeverity("success"));

				dispatch(setSnackbarText("Event updated successfully."));
			} else {
				throw new Error(response.data.message || "Update failed");
			}
		} catch (error) {
			console.error("❌ Error saving event:", error);

			dispatch(setSnackbarOpen(true));

			dispatch(setSnackbarSeverity("error"));

			dispatch(setSnackbarText("Failed to update event."));
		} finally {
			setSavingEvent(false);

			setSaveSuccess(false);
		}
	};

	const handleValidateAndSaveItems = async () => {
		if (!event || !selectedSession) {
			return {
				success: false,
				message: "No event or session selected.",
			};
		}

		const eventId = event.id;

		try {
			setSavingItems(true);

			const parsedItems = localItems
				.map((item) => parseInt(item, 10))
				.filter((item) => !isNaN(item));

			const payload: any = {
				actual_items: parsedItems,
			};

			if (oopTouched) {
				const n = Number(oop);

				payload.proposed_oop =
					oop.trim() === "" || !Number.isFinite(n)
						? null
						: n.toFixed(2);
			} else if (clearOopOnSave) {
				payload.proposed_oop = null;
			}

			if ((actualFee ?? "").trim() === "") {
				payload.actual_fee = null;
			} else {
				const n = Number(actualFee);

				payload.actual_fee = Number.isFinite(n) ? n.toFixed(2) : null;
			}

			const response = await globalAxios.put(
				`${SERVER_URL}/user/events/${eventId}`,
				payload,
			);

			if (response.status === 200) {
				playSuccessSave();

				setSaveItemsSuccess(true);

				const {
					invalid_items,
					updatedEvent,
					schedules: returnedSchedules,
				} = response.data;

				if (
					Array.isArray(returnedSchedules) &&
					returnedSchedules.length > 0
				) {
					dispatch(upsertSchedules(returnedSchedules));
				}

				if (!updatedEvent) {
					console.warn("[SaveAll] No updatedEvent returned.");

					return {
						success: true,
						invalid_items,
						message: response.data.message,
					};
				}

				const updatedEvents = selectedSession.events.map((ev) =>
					ev.id === updatedEvent.id ? updatedEvent : ev,
				);

				dispatch(
					setSelectedSession({
						...selectedSession,
						events: [...updatedEvents],
					}),
				);

				dispatch(setSelectedEvent(updatedEvent));

				dispatch(
					updateEvent({
						sessionId: selectedSession.id.toString(),
						eventId: updatedEvent.id,
						updates: updatedEvent,
					}),
				);

				if (
					typeof updatedEvent.proposed_oop !== "undefined" &&
					updatedEvent.proposed_oop !== null &&
					String(updatedEvent.proposed_oop).trim() !== ""
				) {
					const asNum = Number(updatedEvent.proposed_oop);

					if (Number.isFinite(asNum)) {
						setOop(asNum.toFixed(2));
						setOopSource("Manual OOP");
					} else {
						setOop(String(updatedEvent.proposed_oop));
						setOopSource("Manual OOP");
					}
				} else {
					const rule = resolveSurgeonBillingRule(
						updatedEvent,
						surgeonBillingRules,
						selectedSession,
					);

					const defaultOop = rule ? Number(rule.default_oop) : null;

					if (defaultOop !== null && Number.isFinite(defaultOop)) {
						setOop(defaultOop.toFixed(2));

						setOopSource("Surgeon billing rule");
					} else {
						setOop("0.00");

						setOopSource("No OOP");
					}
				}

				setOopTouched(false);

				setClearOopOnSave(false);

				setActualFee(
					updatedEvent.actual_fee != null
						? String(updatedEvent.actual_fee)
						: "",
				);

				dispatch(setSnackbarOpen(true));

				dispatch(setSnackbarSeverity("success"));

				dispatch(setSnackbarText("Changes saved."));

				return {
					success: true,
					invalid_items,
					message: response.data.message,
				};
			}

			throw new Error(response.data?.message || "Save failed");
		} catch (error: any) {
			console.error("❌ Error saving event:", error);

			if (error?.response?.status === 400) {
				const invalid_items = error.response.data?.invalid_items || [];

				dispatch(setSnackbarOpen(true));

				dispatch(setSnackbarSeverity("error"));

				dispatch(
					setSnackbarText(
						error.response.data?.message || "Validation failed.",
					),
				);

				return {
					success: false,
					invalid_items,
					message:
						error.response.data?.message || "Validation failed.",
				};
			}

			dispatch(setSnackbarOpen(true));

			dispatch(setSnackbarSeverity("error"));

			dispatch(setSnackbarText("An unexpected error occurred."));

			return {
				success: false,
				message: "An unexpected error occurred.",
			};
		} finally {
			setSavingItems(false);

			setTimeout(() => {
				setSaveItemsSuccess(false);
			}, 1500);
		}
	};

	const handleDeleteEvent = () => {
		setDeletingEvent(true);

		setTimeout(() => {
			setDeletingEvent(false);
		}, 2000);
	};

	const handlePatientClick = async (event) => {
		if (!event.patient.id) {
			return;
		}

		const patientId = event.patient.id;

		dispatch(setLoadingDetail(true));

		dispatch(
			setSelectedItem({
				...event.patient,
				type: "patient",
			}),
		);

		navigate("/search");

		try {
			const res = await globalAxios.get(
				`${SERVER_URL}/search/patient/${patientId}`,
			);

			dispatch(
				setSelectedItem({
					...res.data,
					type: "patient",
				}),
			);

			dispatch(setLoadingDetail(false));
		} catch (err) {
			console.error("Failed to fetch patient with linked events", err);
		}
	};

	const handleApplyDefaultOOP = () => {
		if (!event) {
			return;
		}

		if (billingSurgeonId === null) {
			dispatch(setSnackbarOpen(true));

			dispatch(setSnackbarSeverity("error"));

			dispatch(
				setSnackbarText(
					"Cannot apply default OOP because no surgeon is available.",
				),
			);

			return;
		}

		if (billingRulesLoadedForSurgeonId !== billingSurgeonId) {
			dispatch(setSnackbarOpen(true));

			dispatch(setSnackbarSeverity("error"));

			dispatch(
				setSnackbarText(
					billingRulesLoadError ||
						"Surgeon billing rules are still loading.",
				),
			);

			return;
		}

		console.log("[OOP] Apply default:", {
			eventId: event.id,
			proposed_oop: event.proposed_oop,
			hasManualOopOverride,
			surgeonId: billingSurgeonId,
			ruleCount: surgeonBillingRules.length,
			matchedBillingRule,
			resolvedDefaultOop,
		});

		if (!matchedBillingRule || resolvedDefaultOop === null) {
			console.warn("[OOP] Apply default: no matching billing rule", {
				eventId: event.id,
				date: event.date_of_service,
				hospitalId:
					event.hospital_id ??
					event.hospital?.id ??
					selectedSession?.hospital_id ??
					selectedSession?.hospital?.id ??
					null,
				surgeonId: billingSurgeonId,
				rules: surgeonBillingRules,
			});

			dispatch(setSnackbarOpen(true));

			dispatch(setSnackbarSeverity("error"));

			dispatch(
				setSnackbarText("No matching surgeon billing rule was found."),
			);

			return;
		}

		setOop(resolvedDefaultOop.toFixed(2));

		setOopSource("Surgeon billing rule");

		setOopTouched(false);

		setClearOopOnSave(hasManualOopOverride);
	};

	return (
		<LocalizationProvider dateAdapter={AdapterDateFns}>
			<Box
				sx={{
					maxWidth: "1200px",
					width: "100%",
					px: 2,
				}}
			>
				<Box
					sx={{
						display: "flex",
						gap: 2,
						mb: 4,
						flexWrap: "wrap",
					}}
				>
					<Box
						sx={{
							display: "flex",
							flex: 1.5,
							minWidth: {
								xs: "100%",
								md: "0",
							},
						}}
					>
						<Button
							variant="outlined"
							onClick={handleBack}
							sx={{
								borderRadius: "8px 0 0 8px",
								borderRight: "none",
								backgroundColor: "grey.200",
								color: "text.primary",
								borderColor: "grey.400",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								minWidth: 56,
								px: 1,
								py: 1.5,
								"&:hover": {
									backgroundColor: "grey.300",
									borderColor: "grey.400",
								},
								borderTopRightRadius: 0,
								borderBottomRightRadius: 0,
							}}
						>
							<ArrowBackIcon />
						</Button>

						<Box
							sx={{
								border: "1px solid #ccc",
								borderLeft: "none",
								borderRadius: "0 8px 8px 0",
								flex: 1,
								backgroundColor: isFundingLens
									? alpha(fundingColour, 0.2)
									: "#fafafa",
								transition: "background-color 200ms ease",
							}}
						>
							<Box
								sx={{
									borderRadius: "0 7px 0 0",
									backgroundColor: (theme) =>
										theme.palette.primary.main,
									color: (theme) =>
										theme.palette.primary.contrastText,
									px: 2,
									pt: 0.2,
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
								}}
							>
								<Typography variant="h6" fontWeight="bold">
									Case Details
								</Typography>

								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 1,
									}}
								>
									{deletingEvent ? (
										<Box
											sx={{
												position: "relative",
												width: 20,
												height: 20,
												display: "inline-block",
											}}
										>
											<DeleteOutlineIcon
												color="error"
												fontSize="small"
												sx={{
													position: "absolute",
													animation:
														"binFlicker 800ms infinite",
													opacity: 1,
												}}
											/>

											<DeleteIcon
												color="error"
												fontSize="small"
												sx={{
													position: "absolute",
													animation:
														"binFlickerAlt 800ms infinite",
													opacity: 0,
												}}
											/>
										</Box>
									) : savingEvent ? (
										<SaveIcon
											color="warning"
											fontSize="small"
											sx={{
												animation:
													"pulseInward 1s infinite",
											}}
										/>
									) : saveSuccess ? (
										<SaveIcon
											color="success"
											fontSize="small"
											sx={{
												animation:
													"fadeOutSuccess 1.5s forwards",
											}}
										/>
									) : changesMade ? (
										<Tooltip title="Unsaved changes">
											<WarningAmberIcon
												color="warning"
												fontSize="small"
											/>
										</Tooltip>
									) : null}

									<IconButton
										size="small"
										disableRipple
										onClick={handleMenuClick}
										sx={{
											color: "inherit",
											padding: 0,
										}}
									>
										<SettingsIcon />
									</IconButton>
								</Box>

								<Menu
									elevation={2}
									anchorEl={anchorMenu}
									open={openMenu}
									onClose={handleMenuClose}
									transformOrigin={{
										horizontal: "right",
										vertical: "top",
									}}
									anchorOrigin={{
										horizontal: "right",
										vertical: "bottom",
									}}
									sx={{
										marginTop: "4px",
									}}
								>
									{!isEditing ? (
										<MenuItem
											onClick={() => {
												dispatch(
													setEventEditMode(true),
												);

												handleMenuClose();
											}}
										>
											Edit
										</MenuItem>
									) : (
										<>
											<MenuItem
												onClick={() => {
													handleSaveEvent();

													dispatch(saveEventEdits());

													handleMenuClose();
												}}
												disabled={!changesMade}
											>
												Save
											</MenuItem>

											<MenuItem
												onClick={() => {
													dispatch(resetEventEdits());

													handleMenuClose();
												}}
											>
												Cancel
											</MenuItem>
										</>
									)}

									<MenuItem
										onClick={() => {
											const confirmed = window.confirm(
												"Are you sure you want to delete this session? This action cannot be undone.",
											);

											if (confirmed) {
												handleDeleteEvent();
												handleMenuClose();
											}
										}}
										sx={{
											color: "error.main",
										}}
									>
										Delete
									</MenuItem>
								</Menu>
							</Box>

							<Box
								sx={{
									px: 2,
									py: 1,
								}}
							>
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										justifyContent: "space-between",
										gap: 2,
									}}
								>
									<Box
										sx={{
											display: "flex",
											alignItems: "center",
											gap: 1,
											minWidth: 0,
										}}
									>
										<PersonIcon />

										<Typography
											sx={{
												cursor: "pointer",
												color: "primary.main",
											}}
											gutterBottom
											onClick={() =>
												handlePatientClick(event)
											}
										>
											{event.patient?.first_name}{" "}
											{event.patient?.last_name}
										</Typography>
									</Box>

									<Box
										sx={{
											display: "flex",
											alignItems: "center",
											gap: 3,
										}}
									>
										<FormControlLabel
											control={
												<Checkbox
													checked={Boolean(
														event.ifc_sent,
													)}
													disabled={
														!event.ifc_required
													}
													onChange={(e) =>
														handleToggleIFCField(
															"ifc_sent",
															e.target.checked,
														)
													}
													size="small"
												/>
											}
											label={
												<Box
													sx={{
														display: "flex",
														alignItems: "center",
													}}
												>
													IFC sent
													{isFundingLens &&
														event.ifc_required &&
														!event.ifc_sent && (
															<Tooltip title="IFC required but not sent">
																<ReportProblemIcon
																	color="warning"
																	fontSize="small"
																	sx={{
																		ml: 0.5,
																	}}
																/>
															</Tooltip>
														)}
												</Box>
											}
											sx={{
												mr: 0,
												whiteSpace: "nowrap",
											}}
										/>

										<FormControlLabel
											control={
												<Checkbox
													checked={Boolean(
														event.ifc_required,
													)}
													onChange={(e) =>
														handleToggleIFCField(
															"ifc_required",
															e.target.checked,
														)
													}
													size="small"
												/>
											}
											label="IFC required"
											sx={{
												mr: 0,
												whiteSpace: "nowrap",
											}}
										/>
									</Box>
								</Box>

								<Box
									sx={{
										px: 2,
										py: 1,
										bgcolor: "#f0f0f0",
										borderRadius: 1,
										mb: isEditing ? 2 : 1,
									}}
								>
									<Typography variant="body2">
										DOB:{" "}
										{event.patient?.date_of_birth || "—"}
									</Typography>

									<Typography variant="body2">
										Address: {event.patient?.address || "—"}
									</Typography>

									<Typography variant="body2">
										Suburb: {event.patient?.suburb || "—"}
									</Typography>

									<Typography variant="body2">
										Postcode:{" "}
										{event.patient?.postcode || "—"}
									</Typography>

									<Box
										sx={{
											display: "flex",
											alignItems: "center",
											gap: 0.5,
										}}
									>
										<Typography variant="body2">
											Medicare:{" "}
											{event.patient?.medicare || "—"}
										</Typography>

										{isFundingLens &&
											!event.patient?.medicare && (
												<Tooltip title="Medicare missing">
													<ReportProblemIcon
														color="warning"
														fontSize="small"
													/>
												</Tooltip>
											)}
									</Box>
								</Box>

								{isEditing ? (
									<Box
										sx={{
											mb: 2,
										}}
									>
										<TextField
											label="Procedure"
											fullWidth
											size="small"
											value={event.procedure || ""}
											onChange={(e) =>
												handleUpdateField(
													"procedure",
													e.target.value,
												)
											}
										/>
									</Box>
								) : (
									<Typography gutterBottom>
										<strong>Procedure:</strong>{" "}
										{event.procedure || "—"}
									</Typography>
								)}

								{isEditing ? (
									<Box
										sx={{
											mb: 2,
										}}
									>
										<Autocomplete
											size="small"
											options={SPECIALTY_OPTIONS_BASE}
											value={event.specialty || ""}
											onChange={(_, newValue) =>
												handleUpdateField(
													"specialty",
													newValue ?? "",
												)
											}
											isOptionEqualToValue={(o, v) =>
												o === v
											}
											renderInput={(params) => (
												<TextField
													{...params}
													label="Specialty"
												/>
											)}
										/>
									</Box>
								) : (
									<Typography gutterBottom>
										<strong>Speciality:</strong>{" "}
										{event.specialty || "—"}
									</Typography>
								)}

								{isEditing ? (
									<Autocomplete
										size="small"
										disableListWrap
										loading={loadingHealthFunds}
										options={healthFunds}
										getOptionLabel={(option) =>
											option?.aliases?.[1] ||
											option?.name ||
											""
										}
										isOptionEqualToValue={(option, value) =>
											option.id === value.id
										}
										value={
											event.patient?.policies?.[0]
												?.healthFund || null
										}
										onChange={(_, newValue) =>
											handleUpdateField(
												"patient.policies.0.healthFund",
												newValue,
											)
										}
										renderInput={(params) => (
											<TextField
												{...params}
												sx={{
													minWidth: 100,
													mb: 2,
												}}
											/>
										)}
										renderOption={(props, option) => (
											<li {...props} key={option.id}>
												{option.aliases[1]}
											</li>
										)}
										ListboxComponent={ListboxComponent}
									/>
								) : (
									<Typography gutterBottom>
										<span
											style={{
												display: "inline-flex",
												alignItems: "center",
												gap: "2px",
											}}
										>
											<strong>Health Fund:</strong>

											{isFundingLens &&
												!event.patient?.policies?.[0]
													?.healthFund?.id && (
													<Tooltip title="Health Fund missing">
														<ReportProblemIcon
															color="warning"
															fontSize="small"
															sx={{
																verticalAlign:
																	"middle",
																ml: 0.25,
																mr: 0.5,
															}}
														/>
													</Tooltip>
												)}
										</span>{" "}
										{event.patient?.policies?.[0]
											?.healthFund?.name ||
											event.patient?.policies?.[0]
												?.healthFund?.aliases?.[1] ||
											"—"}
									</Typography>
								)}

								{isEditing ? (
									<Box
										sx={{
											mb: 2,
										}}
									>
										<TextField
											label="Fund Number"
											fullWidth
											size="small"
											value={
												event.patient?.policies?.[0]
													?.health_fund_no || ""
											}
											onChange={(e) =>
												handleUpdateField(
													"patient.policies.0.health_fund_no",
													e.target.value,
												)
											}
										/>
									</Box>
								) : (
									<Typography gutterBottom>
										<strong>Fund No.:</strong>{" "}
										{event.patient?.policies?.[0]
											?.health_fund_no || "—"}
									</Typography>
								)}

								{isEditing ? (
									<Autocomplete
										size="small"
										disableListWrap
										loading={loadingAnaesthetists}
										options={anaesthetists}
										getOptionLabel={(option) => option.name}
										isOptionEqualToValue={(option, value) =>
											option.id === value.id
										}
										value={event.anaesthetist || null}
										onChange={(_, newValue) =>
											dispatch(
												updateEventField({
													field: "anaesthetist",
													value: newValue,
												}),
											)
										}
										renderInput={(params) => (
											<TextField
												{...params}
												sx={{
													minWidth: 400,
													mb: 2,
												}}
											/>
										)}
										renderOption={(props, option) => (
											<li {...props} key={option.id}>
												{option.name}
											</li>
										)}
										ListboxComponent={ListboxComponent}
									/>
								) : (
									<Typography gutterBottom>
										<strong>Anaesthetist:</strong>{" "}
										{event.anaesthetist?.name || "—"}
									</Typography>
								)}
							</Box>
						</Box>
					</Box>
				</Box>

				<Box
					sx={{
						mt: 3,
						pt: 0,
						pb: 2,
						border: "1px solid #ccc",
						borderRadius: 2,
					}}
				>
					<Box
						sx={{
							borderRadius: "7px 7px 0 0",
							backgroundColor: (theme) =>
								theme.palette.primary.main,
							color: (theme) =>
								theme.palette.primary.contrastText,
							px: 2,
							pt: 0.2,
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
						}}
					>
						<Typography
							variant="h6"
							fontWeight="bold"
							color="white"
						>
							Item Numbers
						</Typography>

						{savingItems ? (
							<SaveIcon
								color="warning"
								fontSize="small"
								sx={{
									animation: "pulseInward 1s infinite",
								}}
							/>
						) : saveItemsSuccess ? (
							<SaveIcon
								color="success"
								fontSize="small"
								sx={{
									animation: "fadeOutSuccess 1.5s forwards",
								}}
							/>
						) : itemChangesMade || feeChangesMade ? (
							<Tooltip title="Unsaved changes">
								<WarningAmberIcon
									color="warning"
									fontSize="small"
								/>
							</Tooltip>
						) : null}
					</Box>

					<Box px={2}>
						<Grid
							container
							spacing={2}
							columnSpacing={3}
							mt={1}
							justifyContent="flex-start"
						>
							<Grid item xs={12} md={3}>
								<Typography
									variant="body2"
									fontWeight="bold"
									sx={{
										mb: 1,
									}}
								>
									Proposed Items
								</Typography>

								<Box
									display="flex"
									flexDirection="column"
									gap={1}
								>
									{JSON.parse(event.proposed_items)?.map(
										(item, index) => (
											<Box key={`proposed-${index}`}>
												<TextField
													value={item}
													disabled
													inputProps={{
														style: {
															paddingTop: 0,
															paddingBottom: 0,
															width: "60px",
														},
													}}
													sx={{
														width: "fit-content",
														"& .MuiOutlinedInput-root":
															{
																pr: 0,
															},
														bgcolor:
															"background.paper",
													}}
													size="small"
													variant="outlined"
												/>
											</Box>
										),
									)}
								</Box>
							</Grid>

							<Grid item xs={12} md={3}>
								<Box
									display="flex"
									alignItems="center"
									gap={1}
									sx={{
										mb: 1,
									}}
								>
									<Typography
										variant="body2"
										fontWeight="bold"
									>
										Actual Items
									</Typography>

									{event.items_validated === true && (
										<Tooltip
											title="All items valid"
											placement="top"
										>
											<CheckCircleIcon
												sx={{
													color: "success.main",
												}}
												fontSize="small"
											/>
										</Tooltip>
									)}

									{event.items_validated === false && (
										<Tooltip
											title="Some items may be invalid"
											placement="top"
										>
											<WarningAmberIcon
												sx={{
													color: "warning.main",
												}}
												fontSize="small"
											/>
										</Tooltip>
									)}
								</Box>

								<Box
									display="flex"
									flexDirection="column"
									gap={1}
								>
									{localItems.map((item, index) => (
										<Box
											key={index}
											display="flex"
											alignItems="center"
											gap={1}
										>
											<TextField
												value={item}
												onChange={(e) =>
													handleItemChange(
														index,
														e.target.value,
													)
												}
												inputProps={{
													style: {
														paddingTop: 0,
														paddingBottom: 0,
														width: "60px",
													},
												}}
												sx={{
													"& .MuiOutlinedInput-root":
														{
															pr: 0,
															"& fieldset": {
																borderColor:
																	item === ""
																		? "grey.400"
																		: validItemNumbers.has(
																					item,
																			  )
																			? "success.main"
																			: "error.main",
																borderWidth: 1,
															},
															"&:hover fieldset":
																{
																	borderColor:
																		item ===
																		""
																			? "grey.400"
																			: validItemNumbers.has(
																						item,
																				  )
																				? "success.main"
																				: "error.main",
																},
															"&.Mui-focused fieldset":
																{
																	borderColor:
																		item ===
																		""
																			? "grey.400"
																			: validItemNumbers.has(
																						item,
																				  )
																				? "success.main"
																				: "error.main",
																},
														},
													bgcolor: "background.paper",
												}}
												size="small"
												variant="outlined"
												InputProps={{
													endAdornment: (
														<IconButton
															size="small"
															color="error"
															sx={{
																padding: 0,
															}}
															onClick={() =>
																handleDeleteItem(
																	index,
																)
															}
														>
															<DeleteIcon />
														</IconButton>
													),
												}}
											/>

											<Tooltip title="Paste from clipboard">
												<IconButton
													size="small"
													sx={{
														p: 0,
													}}
													onClick={async () => {
														try {
															const text =
																await navigator.clipboard.readText();

															if (text?.trim()) {
																handleItemChange(
																	index,
																	text.trim(),
																);
															}
														} catch (err) {
															console.error(
																"Paste failed:",
																err,
															);
														}
													}}
												>
													<ContentPasteIcon fontSize="small" />
												</IconButton>
											</Tooltip>
										</Box>
									))}

									<Box
										display="flex"
										alignItems="center"
										gap={1}
									>
										<TextField
											value={newItem}
											onChange={(e) => {
												const value = e.target.value;

												if (/^\d*$/.test(value)) {
													setNewItem(value);
												}
											}}
											onKeyDown={(e) => {
												if (e.key === "Enter") {
													handleAddItem();
												}
											}}
											placeholder="Add"
											inputProps={{
												inputMode: "numeric",
												pattern: "[0-9]*",
												style: {
													paddingTop: 0,
													paddingBottom: 0,
													width: "60px",
												},
											}}
											sx={{
												"& .MuiOutlinedInput-root": {
													pr: 0,
												},
												bgcolor: "background.paper",
											}}
											size="small"
											variant="outlined"
											InputProps={{
												endAdornment: (
													<IconButton
														size="small"
														color="primary"
														sx={{
															padding: 0,
														}}
														onClick={handleAddItem}
													>
														<AddCircleIcon />
													</IconButton>
												),
											}}
										/>

										<Tooltip title="Paste from clipboard">
											<IconButton
												size="small"
												sx={{
													p: 0,
												}}
												onClick={async () => {
													try {
														const text =
															await navigator.clipboard.readText();

														const trimmed =
															text.trim();

														if (
															/^\d+$/.test(
																trimmed,
															)
														) {
															setNewItem(trimmed);
														}
													} catch (err) {
														console.error(
															"Failed to paste:",
															err,
														);
													}
												}}
											>
												<ContentPasteIcon fontSize="small" />
											</IconButton>
										</Tooltip>
									</Box>
								</Box>
							</Grid>

							<Grid item xs={12} md={3}>
								<Typography
									variant="body2"
									fontWeight="bold"
									sx={{
										mb: 1,
									}}
								>
									Assistant Fee Estimate
								</Typography>

								<Typography
									variant="body2"
									sx={{
										mb: 2,
									}}
								>
									${finalFee.toFixed(2)}
								</Typography>

								<Typography
									variant="caption"
									color="text.secondary"
									sx={{
										display: "block",
										mb: 1,
										whiteSpace: "pre-line",
									}}
								>
									{oopTouched ? (
										<>
											<b>OOP:</b>
											{"\n"}
											{"Includes overridden OOP."}
										</>
									) : (
										<>
											<b>OOP:</b>
											{"\n"}
											{oopSource}
										</>
									)}
								</Typography>

								<TextField
									value={oop}
									onChange={(e) => {
										const v = e.target.value;

										if (/^\d*\.?\d*$/.test(v)) {
											if (!oopTouched) {
												setOopTouched(true);
											}

											setOop(v);

											setClearOopOnSave(false);
										}
									}}
									onBlur={() => {
										const norm = normaliseCurrency(oop);

										if (norm === "") {
											if (resolvedDefaultOop !== null) {
												setOop(
													resolvedDefaultOop.toFixed(
														2,
													),
												);

												setOopSource(
													"Surgeon billing rule",
												);
											} else {
												setOop("0.00");

												setOopSource("No OOP");
											}

											setOopTouched(false);

											setClearOopOnSave(
												hasManualOopOverride,
											);

											return;
										}

										setOop(norm);

										console.log("[OOP] blur →", norm);
									}}
									placeholder="0.00"
									size="small"
									sx={{
										maxWidth: 160,
										bgcolor: "background.paper",
									}}
									inputProps={{
										inputMode: "decimal",
									}}
									InputProps={{
										startAdornment: (
											<span
												style={{
													marginRight: 6,
												}}
											>
												$
											</span>
										),
									}}
								/>

								<Button
									variant="text"
									size="small"
									onClick={handleApplyDefaultOOP}
									disabled={
										billingSurgeonId === null ||
										billingRulesLoading ||
										billingRulesLoadedForSurgeonId !==
											billingSurgeonId
									}
									sx={{
										mt: 0.5,
										px: 0.5,
										textTransform: "none",
									}}
								>
									Apply default
								</Button>

								<Typography
									variant="body2"
									fontWeight="bold"
									sx={{
										mt: 1,
										mb: 0.5,
									}}
								>
									Actual Fee
								</Typography>

								<TextField
									value={actualFee}
									onChange={(e) => {
										const v = e.target.value;

										if (/^\d*\.?\d*$/.test(v)) {
											setActualFee(v);

											setActualFeeDirty(true);
										}
									}}
									onBlur={() => {
										const clean = (actualFee ?? "").replace(
											/[^\d.]/g,
											"",
										);

										const norm =
											clean === ""
												? ""
												: Number(clean).toFixed(2);

										setActualFee(norm);
									}}
									placeholder="0.00"
									size="small"
									sx={{
										maxWidth: 160,
										bgcolor: "background.paper",
									}}
									inputProps={{
										inputMode: "decimal",
									}}
									InputProps={{
										startAdornment: (
											<span
												style={{
													marginRight: 6,
												}}
											>
												$
											</span>
										),
									}}
								/>
							</Grid>

							<Grid item xs={12} md={3}>
								<Box
									display="flex"
									flexDirection="column"
									alignItems="flex-start"
									gap={1.5}
								>
									<Button
										variant="contained"
										color="primary"
										size="small"
										disableElevation
										disabled={savingItems}
										onClick={handleValidateAndSaveItems}
										sx={{
											minWidth: 0,
											px: 1.2,
											py: 0.5,
											fontSize: "0.9rem",
											lineHeight: 1.1,
											textTransform: "none",
											whiteSpace: "nowrap",
										}}
										startIcon={
											savingItems ? (
												<CircularProgress
													size={10}
													color="inherit"
													sx={{
														mr: 0.5,
													}}
												/>
											) : null
										}
									>
										{savingItems
											? "Saving..."
											: "Validate & Save"}
									</Button>

									{itemChangesMade && !savingItems && (
										<Button
											variant="contained"
											color="warning"
											size="small"
											disableElevation
											disabled={savingItems}
											onClick={handleCancelItemChanges}
											sx={{
												minWidth: 0,
												mt: 2,
												mb: "auto",
												px: 1.2,
												py: 0.5,
												fontSize: "0.9rem",
												lineHeight: 1.1,
												textTransform: "none",
												whiteSpace: "nowrap",
											}}
										>
											Cancel Changes
										</Button>
									)}
								</Box>
							</Grid>
						</Grid>
					</Box>
				</Box>
			</Box>
		</LocalizationProvider>
	);
};

export default EventView;
